import { useEffect, useState } from 'react';
import { fetchWithAuth, getAuthStore } from '@/lib/api/client';

// Share the one short request across the launch preview and transcript handoff.
// Nothing is persisted; account/session identity isolates cached request text.
const cache = new Map();
const CACHE_MS = 60000;

export function startupSpeechMode(phase) {
  if (['credits', 'service', 'deployment', 'opening'].includes(phase)) return null;
  return ['resuming', 'connecting'].includes(phase) ? phase : 'starting';
}

export default function useWorkStartupMessages({ prompt, phase, accountId }) {
  const mode = startupSpeechMode(phase);
  const request = typeof prompt === 'string' ? prompt.trim().slice(0, 2000) : '';
  const generation = getAuthStore().getState()._sessionGeneration;
  const key = mode && accountId && request ? JSON.stringify([accountId, generation, mode, request]) : null;
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!key) return undefined;
    let active = true;
    for (const [cachedKey, entry] of cache) if (entry.expiresAt <= Date.now()) cache.delete(cachedKey);
    let entry = cache.get(key);
    if (!entry) {
      while (cache.size >= 24) cache.delete(cache.keys().next().value);
      const isRequestCurrent = () => {
        const auth = getAuthStore().getState();
        return auth.user?.id === accountId && auth._sessionGeneration === generation;
      };
      entry = {
        expiresAt: Date.now() + CACHE_MS,
        promise: fetchWithAuth('/json/work/startup-messages', {
          method: 'POST', body: { prompt: request, mode }, timeoutMs: 6500,
          networkRetries: 0, isRequestCurrent,
        }).then(data => ({
          messages: Array.isArray(data?.messages) ? data.messages.filter(text => typeof text === 'string' && text.length <= 160).slice(0, 1) : [],
          readyAt: Date.now(),
        })).catch(() => ({ messages: [], readyAt: Date.now() })),
      };
      cache.set(key, entry);
    }
    entry.promise.then(value => { if (active) setResult({ key, ...value }); });
    return () => { active = false; };
  }, [key, accountId, generation, request, mode]);

  return key && result?.key === key ? result : null;
}
